import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles } from 'lucide-react';
import { ReleaseEntry } from '../../data/releaseNotes';

interface WhatsNewModalProps {
  isOpen: boolean;
  release: ReleaseEntry;
  onDismiss: () => void;
  onViewAll: () => void;
}

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const WhatsNewModal: React.FC<WhatsNewModalProps> = ({ isOpen, release, onDismiss, onViewAll }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const viewAllRef = useRef<HTMLButtonElement>(null);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => viewAllRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen) return;

    const handleFocusTrap = (event: FocusEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        viewAllRef.current?.focus();
      }
    };

    document.addEventListener('focusin', handleFocusTrap);
    return () => document.removeEventListener('focusin', handleFocusTrap);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onDismiss]);

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 overlay"
        aria-hidden="true"
        onClick={onDismiss}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="whats-new-modal-title"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div
          className="bg-white dark:bg-cap-dark rounded-xl shadow-2xl max-w-[calc(100%-2rem)] sm:max-w-md w-full p-4 sm:p-6 transform transition-all duration-300 animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header icon */}
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
          </div>

          {/* Title */}
          <h2
            id="whats-new-modal-title"
            className="text-xl sm:text-2xl font-bold text-center text-gray-900 dark:text-warm-white mb-1"
          >
            What's New!
          </h2>

          {/* Version + date */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="px-2 py-0.5 text-xs font-semibold bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
              {release.version}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {formatDate(release.date)}
            </span>
          </div>

          {/* Headline */}
          <p className="text-center text-gray-700 dark:text-gray-300 font-medium mb-4">
            {release.headline}
          </p>

          {/* Highlights */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6 max-h-60 overflow-y-auto">
            <ul className="space-y-2.5">
              {release.highlights.map((highlight, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span className="flex-shrink-0" role="img" aria-hidden="true">{highlight.emoji}</span>
                  <span>{highlight.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col space-y-2">
            <button
              ref={viewAllRef}
              type="button"
              onClick={onViewAll}
              className="w-full min-h-[48px] py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 primary btn-primary"
            >
              See All Updates
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="w-full min-h-[48px] py-2 px-4 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:ring-gray-600 rounded-lg"
            >
              Got It
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default WhatsNewModal;
